type PictureTimestampFields = {
  /** Frozen once resolved — never bump when flags/markup change updatedAt. */
  displayTimestamp?: string | number | Date | null;
  createdDate?: string | number | Date;
  createdAt?: string | number | Date;
  updatedAt?: string | number | Date;
  answer?: { createdAt?: string | number | Date };
};

/**
 * Stable image overlay time. Resolved once per picture and cached on `displayTimestamp`
 * so flag apply / markup / rotate (which bump `updatedAt`) do not change what the user sees.
 */
export function getPictureDisplayTimestamp(
  picture: PictureTimestampFields | null | undefined,
  answerCreatedAt?: string | number | Date | null,
): string | number | Date | null | undefined {
  if (!picture) {
    return answerCreatedAt ?? null;
  }

  if (picture.displayTimestamp != null && picture.displayTimestamp !== '') {
    return picture.displayTimestamp;
  }

  const raw =
    picture.createdDate ??
    picture.createdAt ??
    // Seed from updatedAt only the first time we see the picture (before actions bump it).
    picture.updatedAt ??
    picture.answer?.createdAt ??
    answerCreatedAt ??
    null;

  if (raw != null && raw !== '') {
    picture.displayTimestamp = raw;
  }

  return raw;
}

/** Flag apply/edit/remove bumps picture `updatedAt`. Show that time immediately, not only after refresh. */
export function setPictureDisplayTimestamp(
  picture: PictureTimestampFields | null | undefined,
  timestamp: string | number | Date | null | undefined,
): void {
  if (!picture || timestamp == null || timestamp === '') {
    return;
  }
  picture.displayTimestamp = timestamp;
}

function pictureList(answer: any): any[] {
  if (!answer) {
    return [];
  }
  if (Array.isArray(answer)) {
    return answer.flatMap((a: any) => a?.pictures || []);
  }
  return answer.pictures || [];
}

/**
 * When dual-monitor postMessage / opener sync replaces an answer, keep any richer local
 * markup the incoming payload is missing (empty / absent), so drawings are not wiped.
 */
export function preservePictureMarkup<T>(previous: T, incoming: T): T {
  if (!incoming || !previous) {
    return incoming;
  }

  const prevById = new Map<number | string, any>();
  for (const pic of pictureList(previous)) {
    if (pic?.id != null) {
      prevById.set(pic.id, pic);
    }
  }

  for (const pic of pictureList(incoming)) {
    if (pic?.id == null) {
      continue;
    }
    const prev = prevById.get(pic.id);
    if (!prev) {
      continue;
    }

    // Keep the frozen overlay time across sync.
    if (prev.displayTimestamp != null && pic.displayTimestamp == null) {
      pic.displayTimestamp = prev.displayTimestamp;
    }

    const incomingMarkup = pic.markup;
    const prevMarkup = prev.markup;
    const incomingEmpty = !Array.isArray(incomingMarkup) || incomingMarkup.length === 0;
    const prevHasMarkup = Array.isArray(prevMarkup) && prevMarkup.length > 0;

    if (incomingEmpty && prevHasMarkup) {
      // Keep drawings (and the post-markup image URL) when the sync payload is stale.
      pic.markup = prevMarkup;
      if (prev.Location) {
        pic.Location = prev.Location;
      }
      if (prev.updatedAt != null) {
        pic.updatedAt = prev.updatedAt;
      }
    }
  }

  return incoming;
}
